import React, { useEffect, useState } from "react";

import UpdatePaymentMethodModal from "@/components/UpdatePaymentMethodModal";

export interface OpenUpdatePaymentDetail {
  isPastDue?: boolean;
  failedAmount?: number;
  failedDate?: string;
}

export const OPEN_UPDATE_PAYMENT_EVENT = "open-update-payment-modal";

/** Fire from any "Update Payment Method" banner CTA to open the payment
 *  modal in-place on the current page (no navigation, no extra click). */
export function openUpdatePaymentModal(detail: OpenUpdatePaymentDetail = {}): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<OpenUpdatePaymentDetail>(OPEN_UPDATE_PAYMENT_EVENT, { detail }));
}

/**
 * App-wide host for UpdatePaymentMethodModal. Mounted once in the layout
 * (alongside the other global banners/modals) so the "Update Payment Method"
 * CTA in any past-due banner can open the full update flow immediately,
 * wherever the user happens to be — instead of routing to /account#payment-method
 * and making them click "Update" again.
 *
 * The modal is self-contained (its own Stripe Elements provider + SetupIntent,
 * reads the user from useAuth, reloads the page on a successful save), so it
 * needs no props beyond the past-due context carried by the open event.
 */
const GlobalUpdatePaymentModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState<OpenUpdatePaymentDetail>({});

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<OpenUpdatePaymentDetail>).detail || {};
      setCtx(detail);
      setOpen(true);
    };
    window.addEventListener(OPEN_UPDATE_PAYMENT_EVENT, onOpen as EventListener);
    return () => window.removeEventListener(OPEN_UPDATE_PAYMENT_EVENT, onOpen as EventListener);
  }, []);

  return (
    <UpdatePaymentMethodModal
      open={open}
      onClose={() => setOpen(false)}
      isPastDue={ctx.isPastDue}
      failedAmount={ctx.failedAmount}
      failedDate={ctx.failedDate}
    />
  );
};

export default GlobalUpdatePaymentModal;
